<template>
  <div class="aa-dialog-wrapper">
    <acv-dialog
      v-model="dialogModel"
      :title="aaDialogTitle"
      submit-text="Close"
      cancel-text=""
      cancel-disable
      size="large"
      inactive-after-submission
      @submitted="handleSubmit"
      @closed="handleClose"
    >
      <template #body>
        <div
          v-for="(issue, index) in combinedIssuesWithProcessedResolutions"
          :key="`aa-issue-${index}`"
          class="aa-issue"
          :class="{ breakline: index > 0 }"
        >
          <p
            class="tw-pt-4 tw-font-bold"
          >
            {{ issue.message }}
          </p>
          <div
            v-if="displayAffectedEntities && hasAffectedEntities(issue)"
            class="tw-pb-4"
          >
            <div
              v-for="([entityType, entities], entityTypeIndex)
                in Object.entries(issue.affectedEntitiesMap)"
              :key="`aa-issue-${index}-affected-entities-${entityTypeIndex}`"
              class="aa-issue-affected-entities-wrapper"
            >
              <div
                v-if="entityTypeIndex > 0"
                class="tw-block"
              />
              <span class="tw-font-bold">
                {{ getAffectedLabel(entities, entityType) }}
              </span>
              <span
                v-if="entities?.length === 1"
              >
                {{ formatAffectedEntity(entities[0]) }}
              </span>
              <ul
                v-else
                class="tw-mb-0 tw-pl-4"
              >
                <li
                  v-for="(entity, entity_index) in entities"
                  :key="`aa-issue-${index}-affected-dealer-${entity_index}`"
                >
                  {{ formatAffectedEntity(entity) }}
                </li>
              </ul>
            </div>
          </div>
          <ol>
            <!-- eslint-disable vue/no-v-html -->
            <li
              v-for="(recommendation, rec_index) in issue.processedResolutions"
              :key="`aa-issue-${index}-rec-${rec_index}`"
              class="aa-issue-rec"
              v-html="recommendation"
            />
            <!-- eslint-enable vue/no-v-html -->
          </ol>
        </div>
        <div
          v-if="!combinedIssuesWithProcessedResolutions
            || combinedIssuesWithProcessedResolutions.length === 0"
          class="aa-issue breakline tw-pb-4"
        >
          <p
            class="tw-pt-4 tw-font-bold"
          >
            No issues found.
          </p>
        </div>
      </template>
    </acv-dialog>
  </div>
</template>

<script>
import { defineComponent } from 'vue';
import { mapGetters } from 'vuex';
import { AcvDialog } from '@acv/shared-vuejs';
import Analytics from '@/mixins/Analytics';

export default defineComponent({
  name: 'AcvAuctionDialog',
  components: {
    AcvDialog,
  },
  mixins: [
    Analytics,
  ],
  props: {
    aaIssues: {
      type: Array,
      default: () => [],
    },
    auctionAccessId: {
      type: [Number, String],
      default: '-1',
    },
    showAADialog: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update:show-a-a-dialog', 'aa-dialog-submit'],
  computed: {
    ...mapGetters({
      isAuthorized: 'isAuthorized',
      currentUser: 'currentUser',
    }),
    aaDialogTitle() {
      return `AuctionACCESS Issue${this.aaIssues.length > 1 ? 's' : ''}`;
    },
    dialogModel: {
      get() {
        return this.showAADialog;
      },
      set(showDialog) {
        this.handleTrack(showDialog);
        this.$emit('update:show-a-a-dialog', showDialog);
      },
    },
    displayAffectedEntities() {
      const userActive = this.currentUser?.active;
      const aggregateAffectedIds = this.aaIssues
        .flatMap((issue) => {
          const affectedEntities = issue.affectedEntities ?? [];
          return affectedEntities
            .map((entity) => entity.auctionAccessId);
        });
      const affectedIDsCount = new Set(aggregateAffectedIds).size;
      return affectedIDsCount > 1 || (affectedIDsCount === 1 && userActive);
    },
    combinedIssuesWithProcessedResolutions() {
      return this.aaIssues.map((issue) => ({
        ...issue,
        affectedEntitiesMap:
          Object.groupBy(issue.affectedEntities ?? [], (entity) => entity.entityType),
        message: issue.message === undefined || issue.message === ''
          ? 'There is an issue with your AuctionACCESS Account' : issue.message,
        processedResolutions: this.processResolutions(issue.resolutions ?? []),
      }));
    },
  },
  mounted() {
    if (this.showAADialog) {
      this.track(this.eventNames.auctionAccess.alerts.dialogShown);
    }
  },
  methods: {
    processResolutions(resolutions) {
      return resolutions.map(({
        instruction,
        phone,
        email,
        link,
        linkName,
      }) => {
        const placeholders = {
          // eslint-disable-next-line no-template-curly-in-string
          '${phone}': phone
            ? `<a href="tel:${phone}">${phone}</a>`
            : '',
          // eslint-disable-next-line no-template-curly-in-string
          '${email}': email
            ? `<a href="mailto:${email}">${email}</a>`
            : '',
          // eslint-disable-next-line no-template-curly-in-string
          '${link}': link
            ? `<a href="${link}" target="_blank" rel="noopener noreferrer">${linkName || link}</a>`
            : '',
        };

        let formattedInstruction = instruction;
        Object.keys(placeholders).forEach((placeholder) => {
          formattedInstruction = formattedInstruction.replace(
            placeholder,
            placeholders[placeholder],
          );
        });

        return formattedInstruction;
      });
    },
    hasAffectedEntities(issue) {
      return Object.keys(issue.affectedEntitiesMap ?? {}).length > 0;
    },
    getAffectedLabel(entities, entityType) {
      const label = entityType === 'Company' ? 'Dealer' : 'User';
      return `${label}${entities.length > 1 ? 's' : ''} affected:`;
    },
    formatAffectedEntity(entity) {
      if (entity.entityName && entity.auctionAccessId) {
        return `${entity.entityName} (AA User ID: ${entity.auctionAccessId})`;
      }
      if (entity.entityName && !entity.auctionAccessId) {
        return `${entity.entityName}`;
      }
      return 'Details not found';
    },
    handleTrack(showDialog) {
      if (showDialog) {
        this.track(this.eventNames.auctionAccess.alerts.dialogShown);
      } else {
        this.track(this.eventNames.auctionAccess.alerts.dialogClosed);
      }
    },
    handleClose() {
      this.dialogModel = false;
    },
    handleSubmit() {
      this.$emit('aa-dialog-submit', this.auctionAccessId);
      this.track(this.eventNames.auctionAccess.alerts.dialogSubmitted);
      this.handleClose();
    },
  },
});
</script>

<style lang="scss" scoped>
.aa-dialog-wrapper {
  text-transform: none;
  width: 100%;

  :deep(a) {
    @apply tw-text-text-link-on-light tw-font-bold;
  }

  :deep(ol) {
    padding-left: 0;
    margin-left: 15px;
  }

  :deep(.acv-dialog-content__header--title) {
    @apply tw-text-text-secondary;

    font-size: 1.25rem;
  }

  :deep(.acv-dialog-content__body) {
    padding-bottom: 0;
  }

  :deep(.acv-dialog-content__footer) {
    justify-content: center;

    @screen tw-md {
      justify-content: flex-end;
    }
  }

  .breakline {
    border-top: 1px solid theme('colors.border.base');
  }
}
</style>
